/**
 * 이상한 방 - 꾸밈 적용(`--gh-*` 덮어쓰기 **하나**)
 *
 * 근거 문서: 로드맵 확정안 3.2절(꾸밈은 규칙 영향 0 · `--gh-*` 덮어쓰기로만 적용) · M9 규칙 4 ·
 *           `ui/gameHud.css` 머리말(시각 언어의 하드룰 - 액센트는 자막 노랑 하나) ·
 *           `game/economy/looks.ts` 머리말("이 파일에는 모양이 없다")
 *
 * ## 꾸밈이 화면에 닿는 길은 이 파일 하나다
 *
 * `looks.ts`는 id와 자리만 갖고 색도 도형도 모른다(경제 모듈이 화면을 알면 서버로 못 옮긴다).
 * 그 반대편이 여기다 - **id → CSS 변수 몇 개**로 옮기고 끝이다. 그래서 꾸밈이 할 수 있는 일의 상한이
 * 아래 `COSMETIC_VARS` 목록의 길이와 정확히 같다. 새 자리를 붙이려면 그 목록을 늘려야 하고,
 * 늘리는 순간 `cosmeticVars.test.ts`와 `visualRules.test.ts`가 그것을 본다.
 *
 * ## 꾸밈이 **건드리지 못하는 것** (로드맵 3.2절)
 *
 * 액센트 노랑(`--gh-accent`) · 먹색 플레이트(`--gh-ink`·`--gh-panel`) · 서체(`--gh-font-*`)는 이 게임의
 * 정체이지 장식이 아니다. 뽑기로 그것을 바꿀 수 있으면 두 사람의 화면이 다른 게임처럼 보이고,
 * 그때 "노란 것이 찾을 것"이라는 이 게임의 유일한 색 규약이 사람마다 달라진다.
 *
 * 그 금지는 주석이 아니라 **목록**이다. `COSMETIC_VARS`에 없는 이름은 여기서 나갈 수 없고,
 * 값도 표에서만 온다(런타임 조립 문자열이 없다). `PROTECTED_VARS`는 그 사실을 테스트가 읽을 수 있게 적어 둔 것이다.
 *
 * ## 표는 옆 파일에 있다
 *
 * id → 값의 표 넷(표식 · 이름표 색 · 결과 테두리 · 봉투 겉면)은 `cosmeticStyles.ts`다. 규칙과 데이터는
 * 고치는 이유가 달라서 갈랐고(그 파일 머리말), **규칙은 전부 여기 남아 있다** - 허용 목록도 금지 목록도
 * 심는 함수도 이 파일이 쥔다. 표가 그 규칙을 지키는지는 `visualRules.test.ts`가 표 전체를 훑어 본다.
 *
 * ## 값도 규칙 안에 있다
 *
 * 표식의 반경은 `gameHud.css`가 허락한 두 단계와 도형(50%)뿐이다 - 꾸밈이라고 알약(999px)을 쓸 수 있으면
 * 반경 규칙이 CSS에만 있고 화면에는 없는 셈이 된다. `visualRules.test.ts`가 이 표의 값을 CSS와 **같은 집합**으로 본다.
 *
 * 이름표 색만 토큰 밖의 색을 쓴다. 그것이 허용되는 이유는 `looks.ts` 머리말 그대로다 - 이름표 색은
 * **플레이어를 구별하는 색**이지 화면의 정체를 바꾸는 색이 아니고, 쓰이는 자리도 내 줄의 테두리·이름 하나뿐이다.
 * 기본(노란 이름표)은 아무 변수도 내지 않는다 - 그래야 아무것도 안 뽑은 사람의 화면이 지금과 **한 픽셀도** 다르지 않다.
 *
 * ## 색이 아닌 둘(별명 · 도장 문구)도 여기서 함께 쥔다
 *
 * 별명은 CSS 변수로 옮길 수 없고(문자열이며 번역 대상이다), 그렇다고 여럿이 뷰에 실을 수도 없다 -
 * 그 뷰는 호스트가 만들고 호스트는 남의 계정 사물함을 모른다(`matchView.ts`). 그래서 **내 것만** 이 모듈이
 * 들고 있고, 참가자 줄·순위표·로비 좌석이 자기 줄을 그릴 때 물어본다. 모듈 수준 상태를 두는 것은
 * `ui/settings.isSettingsOpen()`이 이미 쓰는 방식이라 새 예외가 아니다.
 * 값을 **id로** 들고 있는 이유는 언어 전환이다 - 글자로 굳혀 두면 언어를 바꾼 뒤에도 옛 언어가 남는다.
 *
 * 페이즈 도장의 문구 세트도 같은 이유로 변수가 아니라 사전 키다(`phaseStampKey`). 뽑은 세트가
 * `phase.<페이즈>.stamp.<id>`를 갖고 있으면 그것을, 없으면 기본 문구를 읽는다 - `hasKey`로 갈리므로
 * 세트가 모든 페이즈를 채울 필요가 없다.
 */

import type { Equipped } from '../game/economy/inventory'
import type { NicknameId } from '../game/economy/titles'
import { hasKey, t } from '../i18n'

import { ENVELOPE_STYLES, FRAME_STYLES, MARKER_STYLES, TAG_COLORS } from './cosmeticStyles'

/* --- 변수 목록 ---------------------------------------------------------------- */

/**
 * 꾸밈이 쓸 수 있는 변수 **전부**. 여섯뿐이고, 여섯 다 CSS가 폴백과 함께 읽는다.
 *
 *  - `--gh-mark-glyph`  사격 표식 안에 찍히는 글자(`content`). 비어 있으면 지금과 같은 빈 고리다
 *  - `--gh-mark-anim`   그 글자의 움직임. SSR 표식 둘만 쓴다(`prefers-reduced-motion`에서 CSS가 끈다)
 *  - `--gh-tag`         내 이름표 색(참가자 줄 테두리 · 순위표 내 줄 · 로비 내 좌석)
 *  - `--gh-mark-radius` 표식 고리의 반경. 값은 CSS의 허용 집합과 같다(머리말)
 *  - `--gh-frame`       결과 카드의 테두리(`gameHud.css`의 `.rs-card::after`). 기본은 값이 없어 테두리도 없다
 *  - `--gh-envelope`    봉투 겉면의 무늬(`envelopeCard.css`의 `.ev-card__front`). 기본은 지금의 크라프트 사선이다
 *
 * 다섯째 자리(스탬프)만 변수가 아니다 - 그것은 색이 아니라 **문구 세트**라 사전을 거친다
 * (`phaseStampKey`). 같은 이유로 별명도 여기 없다.
 */
export const COSMETIC_VARS = [
  '--gh-mark-glyph',
  '--gh-mark-anim',
  '--gh-tag',
  '--gh-mark-radius',
  '--gh-frame',
  '--gh-envelope',
] as const

export type CosmeticVarName = (typeof COSMETIC_VARS)[number]

/**
 * 꾸밈이 **절대 내지 못하는** 변수. 테스트가 읽는 목록이고, 여기 있는 이름이 `COSMETIC_VARS`에
 * 하나라도 들어오면 그것은 로드맵 3.2절을 어긴 것이다.
 */
export const PROTECTED_VARS: readonly string[] = [
  '--gh-accent',
  '--gh-accent-shade',
  '--gh-ink',
  '--gh-panel',
  '--gh-panel-soft',
  '--gh-fg',
  '--gh-warn',
  '--gh-font-display',
  '--gh-font-body',
  '--gh-font-numeric',
  '--gh-radius',
  '--gh-radius-plate',
]

/** 한 벌의 덮어쓰기. 없는 자리는 키가 아예 없다(= CSS의 폴백이 그대로 산다). */
export type CosmeticVars = Readonly<Partial<Record<CosmeticVarName, string>>>

/* --- 스탬프 문구 세트 ------------------------------------------------------------- */

/**
 * 페이즈 도장의 문구 세트. 변수가 아니라 **사전 키**다 - 문구는 번역 대상이라 CSS로 나갈 수 없다.
 *
 * 조립 규칙은 `phase.<페이즈>.stamp.<꾸밈 id>`이고, 그 키가 없으면 기본(`phase.<페이즈>.stamp`)이다.
 * 기본 세트(`stampBasic`)가 키를 갖지 않는 이유가 그것이다 - 아무것도 안 뽑은 화면이 지금 그대로여야 한다.
 */
let equippedStamp: string = ''

/**
 * 그 세트에서 이 페이즈 도장이 읽을 키. **순수 함수다**(테스트가 DOM 없이 부른다).
 * 없는 세트·없는 페이즈는 조용히 기본으로 떨어진다(`hasKey`) - 꾸밈 하나 때문에 도장 자리에
 * `[[key]]`가 뜨는 것은 이 기능이 감당할 실패가 아니다.
 */
export function phaseStampKeyFor(stamp: string, phase: string): string {
  const base = `phase.${phase}.stamp`
  if (stamp === '') return base
  const decorated = `${base}.${stamp}`
  return hasKey(decorated) ? decorated : base
}

/** 지금 끼운 세트로 물어본 것. 화면(`ui/juice.ts`)이 부르는 자리다. */
export function phaseStampKey(phase: string): string {
  return phaseStampKeyFor(equippedStamp, phase)
}

/* --- 변환 -------------------------------------------------------------------- */

/**
 * 끼운 것 → 덮어쓸 변수들. **모르는 id는 조용히 넘긴다** - 저장값이 앞선 판의 것일 수 있고,
 * 꾸밈 하나 때문에 화면이 서지 않는 것은 이 기능이 감당할 만한 실패가 아니다.
 */
export function cosmeticVarsFor(equipped: Equipped): CosmeticVars {
  const vars: Partial<Record<CosmeticVarName, string>> = {}

  const marker = MARKER_STYLES[equipped.marker]
  if (marker !== undefined && marker.glyph !== '') {
    vars['--gh-mark-glyph'] = marker.glyph
    if (marker.radius !== undefined) vars['--gh-mark-radius'] = marker.radius
    if (marker.anim !== undefined) vars['--gh-mark-anim'] = marker.anim
  }

  const tag = TAG_COLORS[equipped.nametag]
  if (tag !== undefined) vars['--gh-tag'] = tag

  const frame = FRAME_STYLES[equipped.frame]
  if (frame !== undefined) vars['--gh-frame'] = frame

  const envelope = ENVELOPE_STYLES[equipped.envelope]
  if (envelope !== undefined) vars['--gh-envelope'] = envelope

  return vars
}

/* --- 적용 -------------------------------------------------------------------- */

/**
 * 변수를 요소에 심는다. **목록에 있는 이름은 매번 전부 손댄다** - 값이 없으면 지우는 것까지가 이 함수의 일이고,
 * 지우지 않으면 표식을 기본으로 되돌린 사람의 화면에 옛 글자가 남는다(사물함에서 갈아 끼우는 순간 보인다).
 */
export function applyCosmeticVars(target: HTMLElement, vars: CosmeticVars): void {
  for (const name of COSMETIC_VARS) {
    const value = vars[name]
    if (value === undefined) target.style.removeProperty(name)
    else target.style.setProperty(name, value)
  }
}

/* --- 별명 -------------------------------------------------------------------- */

/** 지금 끼운 별명. 화면 세 곳(참가자 줄 · 순위표 · 로비 좌석)이 **자기 줄에만** 붙인다. */
let equippedNickname: NicknameId | null = null

/** 끼운 것 전부를 한 줄로 적용한다. 앱이 부르는 유일한 입구다(`main.ts`). */
export function applyCosmetics(target: HTMLElement, equipped: Equipped): void {
  applyCosmeticVars(target, cosmeticVarsFor(equipped))
  equippedNickname = equipped.nickname
  // 도장 문구도 색이 아니라 글자라 여기서 함께 쥔다(`phaseStampKey`).
  equippedStamp = equipped.stamp
}

/** 내 별명 문구. 없으면 `null`이고, 그때 화면은 줄을 만들지 않는다(빈 줄은 자리만 먹는다). */
export function myNicknameLabel(): string | null {
  if (equippedNickname === null) return null
  return t(`nickname.${equippedNickname}`)
}

/**
 * 되돌리기. **지금 부르는 것은 테스트뿐이다** - 앱은 사물함에서 갈아 끼울 때마다 `applyCosmetics`를
 * 다시 부르고 그쪽이 없는 값을 지우므로(`applyCosmeticVars`), 따로 비울 자리가 없다.
 * 화면을 통째로 다시 세우는 쪽(다른 계정으로 갈아타는 길 같은 것)이 생기면 그때 이것을 불러라.
 */
export function resetCosmetics(target?: HTMLElement): void {
  equippedNickname = null
  equippedStamp = ''
  if (target !== undefined) applyCosmeticVars(target, {})
}
